import React, { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";

// Critical pages loaded eagerly (Landing is the LCP page)
import Landing from "./pages/Landing";

// Lazy load Auth - not needed for landing page initial render
const Auth = lazy(() => import("./pages/Auth"));

// Lazy load all other pages to reduce initial bundle size
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Recipes = lazy(() => import("./pages/Recipes"));
const NewRecipe = lazy(() => import("./pages/NewRecipe"));
const EditRecipe = lazy(() => import("./pages/EditRecipe"));
const RecipeDetail = lazy(() => import("./pages/RecipeDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const AIAssistant = lazy(() => import("./pages/AIAssistant"));
const MealPlanner = lazy(() => import("./pages/MealPlanner"));
const GroceryLists = lazy(() => import("./pages/GroceryLists"));
const Pantry = lazy(() => import("./pages/Pantry"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Support = lazy(() => import("./pages/Support"));
const Terms = lazy(() => import("./pages/Terms"));
const NativePurchaseRestricted = lazy(() => import("./pages/NativePurchaseRestricted"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Screenshots = lazy(() => import("./pages/Screenshots"));

const queryClient = new QueryClient();

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const NATIVE_OAUTH_HOST = "https://c6121c69-0ed2-40cf-b8e2-39513bcea01e.lovableproject.com";

const NativeOAuthFallback = () => {
  useEffect(() => {
    let isNative = false;
    try {
      isNative = Capacitor.isNativePlatform();
    } catch {
      isNative = false;
    }

    if (!isNative) {
      window.location.replace("/");
      return;
    }

    const callbackPath = `/~oauth${window.location.search}${window.location.hash}`;
    window.location.replace(`${NATIVE_OAUTH_HOST}${callbackPath}`);
  }, []);

  return <PageLoader />;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/auth" element={user ? <Navigate to="/" replace /> : <Auth />} />
        <Route path="/" element={user ? <Dashboard /> : <Landing />} />
        <Route path="/recipes" element={user ? <Recipes /> : <Navigate to="/auth" replace />} />
        <Route path="/recipes/new" element={user ? <NewRecipe /> : <Navigate to="/auth" replace />} />
        <Route path="/recipes/:id/edit" element={user ? <EditRecipe /> : <Navigate to="/auth" replace />} />
        <Route path="/recipes/:id" element={user ? <RecipeDetail /> : <Navigate to="/auth" replace />} />
        <Route path="/profile" element={user ? <Profile /> : <Navigate to="/auth" replace />} />
        <Route path="/assistant" element={user ? <AIAssistant /> : <Navigate to="/auth" replace />} />
        <Route path="/planner" element={user ? <MealPlanner /> : <Navigate to="/auth" replace />} />
        <Route path="/pantry" element={user ? <Pantry /> : <Navigate to="/auth" replace />} />
        <Route path="/groceries" element={user ? <GroceryLists /> : <Navigate to="/auth" replace />} />
        <Route path="/pricing" element={user ? <Pricing /> : <Navigate to="/auth" replace />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/native-purchase-restricted" element={user ? <NativePurchaseRestricted /> : <Navigate to="/auth" replace />} />
        <Route path="/screenshots" element={<Screenshots />} />
        <Route path="/~oauth/*" element={<NativeOAuthFallback />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <AuthProvider>
        <SubscriptionProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppRoutes />
              <PWAInstallPrompt />
            </BrowserRouter>
          </TooltipProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
